import { useEffect, useMemo, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import { getEquipmentCalendar, selectEquipmentCalendar, selectEquipmentCalendarLoading } from "store/boats"

/**
 * Parse time string (e.g., "04:15 PM") to time slot index
 * Time slots start at 12 AM (index 0) and go to 11 PM (index 23)
 * @param {string} timeStr - Time string in format "HH:MM AM/PM"
 * @returns {number|null} - Time slot index or null if out of range
 */
const parseTimeToSlotIndex = (timeStr) => {
  if (!timeStr) return null

  try {
    // Parse time string like "04:15 PM"
    const match = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i)
    if (!match) return null

    let hour = Number.parseInt(match[1], 10)
    const ampm = match[3].toUpperCase()

    // Convert to 24-hour format
    if (ampm === "PM" && hour !== 12) {
      hour += 12
    } else if (ampm === "AM" && hour === 12) {
      hour = 0
    }

    // Time slots now start at 12 AM (hour 0)
    // So slot index = hour itself (0-23)
    return hour
  } catch (error) {
    console.error("Error parsing time:", timeStr, error)
    return null
  }
}

/**
 * Custom hook for managing calendar data fetching and processing
 * @param {string} equipmentId - Equipment ID
 * @param {string} viewMode - Current view mode (day/week/month)
 * @param {Date} selectedDate - Currently selected date
 * @param {string} activeFilter - Active filter (all/with_bookings/without_bookings)
 * @param {string} selectedAsset - Selected asset ID or "all"
 * @returns {Object} - Calendar data, loading state, and total bookings count
 */
export const useCalendarData = (equipmentId, viewMode, selectedDate, activeFilter, selectedAsset) => {
  const dispatch = useDispatch()
  const calendarData = useSelector(selectEquipmentCalendar)
  const loading = useSelector(selectEquipmentCalendarLoading)
  const prevParamsRef = useRef(null)

  // Memoize params to prevent double API calls when multiple dependencies change
  const apiParams = useMemo(() => {
    // Determine asset filter based on view mode
    let assetFilter
    if (viewMode === "month") {
      assetFilter = selectedAsset === "all" ? "all" : "single_asset"
    } else {
      assetFilter = activeFilter
    }

    // For week view, use the Monday of the selected week
    let dateToSend = selectedDate
    if (viewMode === "week") {
      const current = new Date(selectedDate)
      const day = current.getDay()
      const diff = day === 0 ? -6 : 1 - day // If Sunday, go back 6 days, otherwise go back to Monday
      const monday = new Date(current)
      monday.setDate(current.getDate() + diff)
      dateToSend = monday
    }

    // Format date as YYYY-MM-DD using local timezone (not UTC)
    const year = dateToSend.getFullYear()
    const month = String(dateToSend.getMonth() + 1).padStart(2, "0")
    const day = String(dateToSend.getDate()).padStart(2, "0")
    const formattedDate = `${year}-${month}-${day}`

    // Build API params based on current state
    return {
      view_type: viewMode,
      date: formattedDate, // YYYY-MM-DD format in local timezone
      asset_filter: assetFilter,
      ...(viewMode === "month" && selectedAsset !== "all" && { asset_id: selectedAsset })
    }
  }, [viewMode, selectedDate, activeFilter, selectedAsset])

  // Fetch calendar data when parameters change
  useEffect(() => {
    if (!equipmentId) return

    // Compare with previous params to avoid duplicate calls
    const paramsString = JSON.stringify(apiParams)
    if (prevParamsRef.current === paramsString) {
      return // Skip if params haven't changed
    }

    prevParamsRef.current = paramsString
    dispatch(getEquipmentCalendar(equipmentId, apiParams))
  }, [dispatch, equipmentId, apiParams])

  // Map API response to component data structure
  const assets = useMemo(() => {
    const viewType = calendarData?.data?.view_type || calendarData?.metadata?.view_type

    // Day view has a different structure: direct assets array (no calendar_grid)
    if (viewType === "day") {
      const assetsData = calendarData?.data?.assets || calendarData?.assets

      if (!assetsData) {
        return []
      }

      // Map day view assets directly
      const result = assetsData
        .map((assetData) => {
          // Use a Map to deduplicate bookings by booking_id
          const bookingsMap = new Map()

          ;(assetData.bookings || []).forEach((booking) => {
            // Only add if not already in map (prevents duplicates)
            if (!bookingsMap.has(booking.booking_id)) {
              // Parse start and end times to calculate startTime and duration
              const startTime = parseTimeToSlotIndex(booking.start_time)
              const endTime = parseTimeToSlotIndex(booking.end_time)
              const duration = endTime !== null && startTime !== null ? endTime - startTime : 1

              bookingsMap.set(booking.booking_id, {
                id: booking.booking_id,
                uuid: booking.booking_uuid,
                date: new Date(booking.booked_date),
                timeRange: booking.time_range,
                tripName: booking.trip_name,
                paymentStatus: booking.payment_status,
                startTime, // Time slot index (0-23 for 12 AM - 11 PM)
                duration // Duration in time slots
              })
            }
          })

          return {
            id: assetData.asset_id,
            number: assetData.asset_number,
            bookings: Array.from(bookingsMap.values())
          }
        })
        .sort((a, b) => a.number.localeCompare(b.number))

      return result
    }

    // Week and Month views use calendar_grid
    const calendarGrid = calendarData?.data?.calendar_grid || calendarData?.calendar_grid

    if (!calendarGrid) {
      return []
    }

    const assetMap = new Map()

    if (viewType === "month") {
      // Month view: days have bookings directly (not nested under assets)
      calendarGrid.forEach((dayData) => {
        const dayDate = new Date(dayData.date)

        // Iterate through bookings for this day
        dayData.bookings?.forEach((booking) => {
          // Each booking has assets array
          booking.assets?.forEach((assetInfo) => {
            const assetId = assetInfo.asset_id

            // Initialize asset in map if not exists
            if (!assetMap.has(assetId)) {
              assetMap.set(assetId, {
                id: assetId,
                number: assetInfo.asset_number,
                bookings: [],
                bookingIds: new Set() // Track booking IDs to prevent duplicates
              })
            }

            // Add booking to this asset
            const asset = assetMap.get(assetId)
            // Only add if not already added (deduplicate by booking ID)
            if (!asset.bookingIds.has(booking.booking_id)) {
              asset.bookingIds.add(booking.booking_id)
              asset.bookings.push({
                id: booking.booking_id,
                uuid: booking.booking_uuid,
                date: dayDate,
                timeRange: booking.time_range,
                tripName: booking.trip_name,
                paymentStatus: booking.payment_status
              })
            }
          })
        })
      })
    } else {
      // Week view: days contain assets, which contain bookings
      calendarGrid.forEach((dayData) => {
        const dayDate = new Date(dayData.date)

        // Iterate through each asset for this day
        dayData.assets?.forEach((assetData) => {
          const assetId = assetData.asset_id

          // Initialize asset in map if not exists
          if (!assetMap.has(assetId)) {
            assetMap.set(assetId, {
              id: assetId,
              number: assetData.asset_number,
              bookings: [],
              bookingIds: new Set() // Track booking IDs to prevent duplicates
            })
          }

          // Add bookings for this asset on this day
          const asset = assetMap.get(assetId)
          assetData.bookings?.forEach((booking) => {
            // Only add if not already added (deduplicate by booking ID)
            if (!asset.bookingIds.has(booking.booking_id)) {
              asset.bookingIds.add(booking.booking_id)
              asset.bookings.push({
                id: booking.booking_id,
                uuid: booking.booking_uuid,
                date: dayDate,
                timeRange: booking.time_range,
                tripName: booking.trip_name,
                paymentStatus: booking.payment_status
              })
            }
          })
        })
      })
    }

    // Convert map to array, remove bookingIds helper, and sort by asset number
    return Array.from(assetMap.values())
      .map((asset) => {
        // Remove the bookingIds Set (used only for deduplication)
        const { bookingIds, ...assetWithoutIds } = asset
        return assetWithoutIds
      })
      .sort((a, b) => a.number.localeCompare(b.number))
  }, [calendarData])

  // Get total bookings count from metadata or data
  const totalBookingsCount = calendarData?.metadata?.upcoming_bookings_count || calendarData?.total_bookings || 0

  return { assets, loading, totalBookingsCount }
}
